import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Lock, Key, Shield, AlertTriangle, CheckCircle2,
    Activity, Settings2, Info, Code2, Globe
} from 'lucide-react'

type TabType = 'overview' | 'domains' | 'demo' | 'docs'

export default function EncryptionPage() {
    const [tab, setTab] = useState<TabType>('overview')
    const [demoInput, setDemoInput] = useState('vendor_core_secret_key_2026')
    const [demoEncrypted, setDemoEncrypted] = useState('')

    const simulateEncrypt = async () => {
        const encoder = new TextEncoder()
        const data = encoder.encode(demoInput)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        setDemoEncrypted(hashArray.map(b => b.toString(16).padStart(2, '0')).join(''))
    }

    const domains = [
        { domain: 'autogest360.es', status: 'active', keyId: 'AES-001', bound: true },
        { domain: 'intranet.elpilar.es', status: 'active', keyId: 'AES-002', bound: true },
        { domain: 'localhost', status: 'dev', keyId: 'DEV-KEY', bound: false },
        { domain: '*.vercel.app', status: 'staging', keyId: 'STG-001', bound: false },
    ]

    const tabs = [
        { id: 'overview' as const, label: 'Overview', icon: <Activity size={16} /> },
        { id: 'domains' as const, label: 'Dominios', icon: <Globe size={16} /> },
        { id: 'demo' as const, label: 'Demo Crypto', icon: <Key size={16} /> },
        { id: 'docs' as const, label: 'Docs', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Lock className="text-neon-blue" /> Encriptación AES-GCM
                </h1>
                <p className="text-gray-500 text-sm">Desencriptado de assets críticos atado al dominio</p>
            </div>

            <FeatureNotEnabled moduleName="Encriptación AES-GCM" />

            {/* Diagrama conceptual */}
            <Card className="mb-6">
                <div className="flex items-center justify-center gap-3 py-4 flex-wrap">
                    {[
                        { label: 'Asset encriptado', sub: '.enc', color: 'border-red-500' },
                        { label: '+', sub: '', color: '' },
                        { label: 'Domain Key', sub: 'SHA-256(domain)', color: 'border-yellow-500' },
                        { label: '→', sub: '', color: '' },
                        { label: 'AES-GCM Decrypt', sub: 'Web Crypto API', color: 'border-neon-blue' },
                        { label: '→', sub: '', color: '' },
                        { label: 'Asset original', sub: '.js / .wasm', color: 'border-neon-green' },
                    ].map((n, i) => ['+ ', '→'].includes(n.label) ? (
                        <span key={i} className="text-gray-600 text-lg">{n.label.trim()}</span>
                    ) : (
                        <div key={i} className={`flex flex-col items-center gap-1 px-4 py-3 rounded-xl border-2 ${n.color} bg-white/[0.02]`}>
                            <span className="text-white text-xs font-mono">{n.label}</span>
                            <span className="text-[10px] text-gray-500">{n.sub}</span>
                        </div>
                    ))}
                </div>
            </Card>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'overview' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">¿Cómo funciona?</h2>
                        <div className="text-gray-400 text-sm space-y-2 leading-relaxed">
                            <p>Los assets críticos (<code className="text-neon-blue bg-neon-blue/5 px-1 rounded">vendor_core.ts</code>, módulos Wasm) se encriptan en build time con <strong className="text-white">AES-256-GCM</strong>.</p>
                            <p>La clave de desencriptado se deriva del <strong className="text-white">dominio actual</strong> (window.location.hostname) usando PBKDF2.</p>
                            <p>Si alguien copia los archivos a otro dominio, <strong className="text-red-400">no podrá desencriptarlos</strong> porque la clave será diferente.</p>
                        </div>
                    </Card>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                            { icon: <Shield size={20} />, title: 'Domain-Bound', desc: 'Key derivada de hostname — assets inútiles fuera del dominio autorizado' },
                            { icon: <Key size={20} />, title: 'AES-256-GCM', desc: 'Encriptación autenticada con Web Crypto API nativa del browser' },
                            { icon: <Lock size={20} />, title: 'Build-Time', desc: 'Encriptación ocurre en CI/CD, sin keys expuestas en el código' },
                        ].map((v, i) => (
                            <Card key={i} className="!p-4">
                                <div className="text-neon-blue mb-2">{v.icon}</div>
                                <h3 className="text-white text-sm font-bold">{v.title}</h3>
                                <p className="text-gray-500 text-xs mt-1">{v.desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {tab === 'domains' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <Globe size={16} className="text-neon-blue" /> Dominios Autorizados
                    </h2>
                    <div className="space-y-2">
                        {domains.map(d => (
                            <div key={d.domain} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {d.bound ? <CheckCircle2 size={14} className="text-neon-green" /> : <AlertTriangle size={14} className="text-yellow-400" />}
                                    <div>
                                        <code className="text-neon-blue text-sm font-mono">{d.domain}</code>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${d.status === 'active' ? 'bg-neon-green/10 text-neon-green' : d.status === 'dev' ? 'bg-blue-500/10 text-blue-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{d.status}</span>
                                            <span className="text-gray-600 text-[10px] font-mono">Key: {d.keyId}</span>
                                        </div>
                                    </div>
                                </div>
                                <span className={`text-xs font-mono ${d.bound ? 'text-neon-green' : 'text-gray-600'}`}>{d.bound ? '🔒 Bound' : '🔓 Open'}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'demo' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <Key size={16} className="text-neon-blue" /> Demo SHA-256 (Web Crypto API)
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <label className="text-gray-500 text-xs mb-1 block">Input</label>
                            <input
                                type="text" value={demoInput} onChange={e => setDemoInput(e.target.value)}
                                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-neon-blue transition-all"
                            />
                        </div>
                        <button onClick={simulateEncrypt} className="px-4 py-2.5 bg-neon-blue/10 text-neon-blue border border-neon-blue/20 rounded-lg text-sm font-medium hover:bg-neon-blue/20 transition-all">
                            <Key size={14} className="inline mr-2" /> Generar SHA-256
                        </button>
                        {demoEncrypted && (
                            <div>
                                <label className="text-gray-500 text-xs mb-1 block">Hash SHA-256</label>
                                <pre className="bg-black/30 rounded-lg p-4 text-xs text-neon-green font-mono overflow-auto border border-white/5 break-all">{demoEncrypted}</pre>
                            </div>
                        )}
                    </div>
                </Card>
            )}

            {tab === 'docs' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">Pipeline de Encriptación</h2>
                        <pre className="bg-black/30 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-auto border border-white/5">{`// Build time (CI/CD)
const key = await deriveKey(DOMAIN, SALT)  // PBKDF2
const encrypted = await encrypt(asset, key) // AES-256-GCM
fs.writeFileSync('vendor_core.enc', encrypted)

// Runtime (browser)
const domain = window.location.hostname
const key = await deriveKey(domain, SALT)
const decrypted = await decrypt(fetched, key)
// Si domain ≠ DOMAIN → decrypt falla → código inutilizable`}</pre>
                    </Card>
                    <Card className="bg-red-500/5 border border-red-500/20">
                        <div className="flex items-center gap-3">
                            <AlertTriangle size={20} className="text-red-400 shrink-0" />
                            <div>
                                <h3 className="text-red-400 font-bold text-sm">⚠️ Limitaciones</h3>
                                <p className="text-gray-400 text-xs mt-1">La protección domain-bound no es infalible: un atacante sofisticado puede parchear el hostname check. Se recomienda combinar con Wasm + integrity checks para máxima seguridad.</p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
